import { POST_PRICE_LOYALTY } from '@modules/dashboard/components/new-post/newposts/constants';

export type LoyaltyOnlyLockShape = {
  loyalty_points?: unknown;
  is_subscription?: boolean;
  is_individual_price?: boolean;
  is_nft?: boolean;
  subscription?: unknown[];
  NFT?: unknown[];
};

type SubscriptionDataLike = {
  loyalty_points?: unknown;
  is_subscription?: boolean;
  subscriptions?: unknown[];
  is_indivudiual_price?: boolean;
  is_nft_added?: boolean;
  nftIDList?: unknown[];
};

type LockedThroughLike = {
  individual?: {
    loyalty_points?: unknown;
    individual_price?: unknown;
    outer_subscription?: unknown[];
    outer_nft?: unknown[];
  };
  subscription?: unknown[];
  nft?: unknown[];
};

export function parseLoyaltyPointsInRange(value: unknown): number | null {
  const parsedValue = Number(value);

  if (
    !Number.isFinite(parsedValue) ||
    !Number.isInteger(parsedValue) ||
    parsedValue < POST_PRICE_LOYALTY.MIN ||
    parsedValue > POST_PRICE_LOYALTY.MAX
  ) {
    return null;
  }

  return parsedValue;
}

export function hasInvalidOptionalLoyaltyPoints(value: unknown): boolean {
  return value != null && parseLoyaltyPointsInRange(value) == null;
}

export function hasOnlyLoyaltyPointsLock(
  data: LoyaltyOnlyLockShape | null | undefined
): boolean {
  const loyaltyPoints = parseLoyaltyPointsInRange(data?.loyalty_points);
  const hasSubscription = !!(
    data?.is_subscription ||
    data?.subscription?.length
  );
  const hasIndividualPrice = !!data?.is_individual_price;
  const hasNft = !!(data?.is_nft || data?.NFT?.length);

  return !!loyaltyPoints && !hasSubscription && !hasIndividualPrice && !hasNft;
}

export function hasOnlyLoyaltyPointsVipOfferLock(
  data: LoyaltyOnlyLockShape | null | undefined
): boolean {
  return hasOnlyLoyaltyPointsLock(data);
}

export function mapSubscriptionDataToLoyaltyLockShape(
  data: SubscriptionDataLike | null | undefined
): LoyaltyOnlyLockShape {
  return {
    loyalty_points: data?.loyalty_points,
    is_subscription: data?.is_subscription,
    is_individual_price: data?.is_indivudiual_price,
    is_nft: data?.is_nft_added,
    subscription: data?.subscriptions || [],
    NFT: data?.nftIDList || []
  };
}

/** Stable key for deduping loyalty-only warnings after the user has acknowledged a lock config. */
export function loyaltyOnlyLockAcknowledgementFingerprint(
  data: SubscriptionDataLike | null | undefined
): string {
  const s = mapSubscriptionDataToLoyaltyLockShape(data);
  const lp = parseLoyaltyPointsInRange(s.loyalty_points);
  const hasSubscription = !!(
    s.is_subscription || (s.subscription && s.subscription.length > 0)
  );
  const hasIndividualPrice = !!s.is_individual_price;
  const hasNft = !!(s.is_nft || (s.NFT && s.NFT.length > 0));
  return JSON.stringify({
    lp,
    hasSubscription,
    hasIndividualPrice,
    hasNft
  });
}

export function mapLockedThroughToLoyaltyLockShape(
  lockedThrough: LockedThroughLike | null | undefined
): LoyaltyOnlyLockShape {
  const individual = lockedThrough?.individual;

  return {
    loyalty_points: individual?.loyalty_points,
    is_individual_price: !!Number(individual?.individual_price),
    subscription: [
      ...(lockedThrough?.subscription || []),
      ...(individual?.outer_subscription || [])
    ],
    NFT: [...(lockedThrough?.nft || []), ...(individual?.outer_nft || [])]
  };
}

export function mapMonetizePayloadToLoyaltyLockShape(
  payload: Record<string, any> | null | undefined,
  priceKey: string
): LoyaltyOnlyLockShape {
  const priceData = payload?.[priceKey] || {};

  return {
    loyalty_points: priceData?.loyalty_points,
    is_subscription: payload?.is_subscription,
    is_individual_price: payload?.is_individual_price,
    is_nft: payload?.is_nft,
    subscription: payload?.subscription || [],
    NFT: payload?.NFT || []
  };
}
